'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

interface FilePreviewsContextValue {
  rememberPreview: (token: string, preview: string) => void;
  getPreview: (token: string) => string | undefined;
}

// Default value keeps consumers working when no provider is mounted: the
// preview simply does not outlive the input that created it.
const FilePreviewsContext = createContext<FilePreviewsContextValue>({
  rememberPreview: () => {},
  getPreview: () => undefined,
});

/**
 * Holds the preview of every uploaded image for the lifetime of the form. File
 * inputs are remounted on each step change, so a preview kept in their own
 * state disappears as soon as the user navigates away and comes back. The
 * previews are object URLs, released when the form unmounts.
 */
export const FilePreviewsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const previews = useRef(new Map<string, string>());

  useEffect(
    () => () => {
      previews.current.forEach((preview) => URL.revokeObjectURL(preview));
      previews.current.clear();
    },
    [],
  );

  const rememberPreview = useCallback((token: string, preview: string) => {
    previews.current.set(token, preview);
  }, []);

  const getPreview = useCallback(
    (token: string) => previews.current.get(token),
    [],
  );

  const value = useMemo(
    () => ({ rememberPreview, getPreview }),
    [rememberPreview, getPreview],
  );

  return (
    <FilePreviewsContext.Provider value={value}>
      {children}
    </FilePreviewsContext.Provider>
  );
};

export const useFilePreviews = () => useContext(FilePreviewsContext);
